import { Stack, Switch, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { VocabularyAttribute } from '../../../types/catalog';
import InputGroup from '../../form/InputGroup';
import InputLabel from '../../form/InputLabel';
import {
  ProductFormValues,
  attributesForCategory,
  generateVariantRows,
  valueLabelsFor,
} from '../../../utils/catalog/productForm';
import ValuePicker from './ValuePicker';
import AxisChips from './AxisChips';

interface Props {
  attributes: VocabularyAttribute[];
}

interface VariesToggleProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

// not a <label>: form.css styles every label inside .input as the row's label cell
function VariesToggle({ id, checked, onChange }: VariesToggleProps) {
  return (
    <Stack
      direction="row"
      onClick={() => onChange(!checked)}
      sx={{
        alignItems: 'center',
        gap: { xs: 0.5, md: 1 },
        flexShrink: 0,
        pl: { xs: 1, md: 2 },
        pr: { xs: 0.5, md: 1.5 },
        cursor: 'pointer',
        userSelect: 'none',
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: theme => (checked ? theme.palette.accent.main : 'transparent'),
        transition: 'background-color 120ms',
        '&:hover': {
          bgcolor: theme =>
            checked ? theme.palette.accent.main : theme.palette.lightGrey.main,
        },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}>
      <Typography
        id={id}
        variant="body2"
        color={checked ? 'text.primary' : 'text.secondary'}>
        Varies
      </Typography>
      <Switch
        size="small"
        checked={checked}
        onClick={e => e.stopPropagation()}
        onChange={(_, next) => onChange(next)}
        inputProps={{ 'aria-labelledby': id }}
      />
    </Stack>
  );
}

/** One row per attribute for the category; “Varies” turns a row into the values it comes in. */
export default function AttributeFields({ attributes }: Props) {
  const { values, setFieldValue } = useFormikContext<ProductFormValues>();
  const applicable = attributesForCategory(attributes, values.category);
  if (!applicable.length) return null;

  const setVaries = (key: string, varies: boolean) => {
    const keys = varies
      ? [...values.varyingKeys, key]
      : values.varyingKeys.filter(k => k !== key);
    const single = values.attributeValues[key];
    const axis = values.axisValues[key] ?? [];
    const axisValues = Object.fromEntries(
      keys.map(k => [
        k,
        values.axisValues[k] ?? (k === key && single ? [single] : []),
      ]),
    );
    const attributeValues = Object.fromEntries(
      Object.entries(values.attributeValues).filter(([k]) => k !== key),
    );
    // switching off keeps the value only when there is exactly one to keep
    if (!varies && axis.length === 1) [attributeValues[key]] = axis;
    setFieldValue('varyingKeys', keys);
    setFieldValue('axisValues', axisValues);
    setFieldValue('attributeValues', attributeValues);
    const rows = values.variants.map(row => ({
      ...row,
      values: Object.fromEntries(
        Object.entries(row.values).filter(([k]) => varies || k !== key),
      ),
    }));
    setFieldValue('variants', generateVariantRows(rows, keys, axisValues));
  };

  return (
    <InputGroup
      name=""
      rows={applicable.map(attribute => {
        const varies = values.varyingKeys.includes(attribute.key);
        return [
          {
            label: attribute.label,
            name: `attributeValues.${attribute.key}`,
            type: 'node',
            node: (
              <div className="input">
                <InputLabel
                  name={`attributeValues.${attribute.key}`}
                  label={attribute.label}
                />
                {/* Stacks, not Boxes: modal.css forces MuiBox-root to 100% on mobile */}
                <Stack direction="row" flex={1} minWidth={0}>
                  <Stack flex={1} minWidth={0} justifyContent="center">
                    {varies ? (
                      <AxisChips attribute={attribute} />
                    ) : (
                      <ValuePicker
                        value={values.attributeValues[attribute.key] ?? ''}
                        options={valueLabelsFor(attribute, values.category)}
                        placeholder={`Select ${attribute.label.toLowerCase()}`}
                        onChange={value =>
                          setFieldValue(
                            `attributeValues.${attribute.key}`,
                            value,
                          )
                        }
                      />
                    )}
                  </Stack>
                  <VariesToggle
                    id={`varies-${attribute.key}`}
                    checked={varies}
                    onChange={checked => setVaries(attribute.key, checked)}
                  />
                </Stack>
              </div>
            ),
          },
        ];
      })}
    />
  );
}
